import React, { useEffect, useState } from 'react'
import DataTable from '../../components/common/DataTable/DataTable';
import Button from '../../components/common/Button/Button';
import { toast, ToastContainer } from 'react-toastify';
import api from '../../services/api';
import { useNavigate } from 'react-router-dom';
import { REACT_APP_API_BASE_URL } from '../../apiurl';
import Input from '../../components/common/Input/Input';
import styles from './NewsFeed.module.css';
import Select from 'react-select';
import { newsService } from '../../services/newsService';
import altNewsImage from '../../assets/Newspaper.jpg'
import { usePermission } from '../../hooks/usePermissions';
import CorporateAnnouncementsTab from './CorporateAnnouncementsTab';
import FullscreenModal from '../../components/common/FullscreenModal/FullscreenModal';
import NewsForm from './NewsForm';
import { formatLocalDate, formatLocalDateTime } from '../../utils/datetime';


const EditIcon = () => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M12 20h9" />
        <path d="M16.5 3.5a2.12 2.12 0 1 1 3 3L7 19l-4 1 1-4Z" />
    </svg>
);

const TrashIcon = () => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
        <polyline points="3 6 5 6 21 6" />
        <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
        <path d="M10 11v6" />
        <path d="M14 11v6" />
        <path d="M9 6V4h6v2" />
    </svg>
);

const TruncatedContent = ({ value, limit = 65 }) => {
    const [isExpanded, setIsExpanded] = useState(false);

    if (!value) return "-";
    if (value.length <= limit) return value;

    return (
        <div className={styles.contentCell}>
            {isExpanded ? value : `${value.substring(0, limit)}...`}
            <button
                className={styles.seeMoreBtn}
                onClick={() => setIsExpanded(!isExpanded)}
            >
                {isExpanded ? " See less" : " See more"}
            </button>
        </div>
    );
};

const NewsFeed = () => {
    const { hasPermission } = usePermission();
    const canViewNews = hasPermission("VIEW_NEWSFEED");
    const canViewCorporate = hasPermission("VIEW_CORPORATE_ANNOUNCEMENT");

    const [isNewsFormOpen, setIsNewsFormOpen] = useState(false);
    const [selectedNewsId, setSelectedNewsId] = useState(null);
    const [activeTab, setActiveTab] = useState(canViewNews ? 'newsfeed' : 'corporate');
    const [newsArticles, setNewsArticles] = useState([]);
    const [selectedNews, setSelectedNews] = useState([]);
    const [page, setPage] = useState(1)
    const [totalPages, setTotalPages] = useState(0);
    const [publishFrom, setPublishFrom] = useState("")
    const [publishTo, setPublishTo] = useState("");
    const [filterPublishStatus, setFilterPublishStatus] = useState("");
    const [titleFilter, setTitleFilter] = useState("");
    const [creditFilter, setCreditFilter] = useState(''); // Changed to null for Select
    const [tagsFilter, setTagsFilter] = useState("");
    const [stockFilter, setStockFilter] = useState("");
    const [sectorFilter, setSectorFilter] = useState("");
    const [industryFilter, setIndustryFilter] = useState("")
    const [allCredits, setAllCredits] = useState([]);
    const [internalTokens, setInternalTokens] = useState([]);
    const [tagSectors, setTagSectors] = useState([]);
    const [tagIndustries, setTagIndustries] = useState([]);

    // hasPermission declared at top
    useEffect(() => {
        // Fetch unique credits for dropdown
        newsService.getUniqueCredits()
            .then(res => {
                if (res.data) {
                    setAllCredits(res.data.map(c => ({ label: c, value: c })));
                } else {
                    console.warn("Credits response structure mismatch:", res);
                }
            })
            .catch(err => console.error("Error fetching credits:", err));
    }, []);

    const handleClearFilters = () => {
        setPublishFrom("");
        setPublishTo("");
        setFilterPublishStatus("");
        setTitleFilter("");
        setCreditFilter('');
        setStockFilter("");
        setIndustryFilter("");
        setSectorFilter("")
        setPage(1);

        // Fetch immediately with cleared values
        fetchNews({
            page: 1,
            publishFrom: "",
            publishTo: "",
            filterPublishStatus: "",
            titleFilter: "",
            creditFilter: '',
            sectorFilter: "",
            stockFilter: "",
            industryFilter: ""
        });
    };

    const limit = 20;

    const handleDelete = (id) => {
        if (!window.confirm("Are you sure you want to delete this news article?")) return;

        api.delete(`/newsfeed/${id}`)
            .then(() => {
                setNewsArticles(prev => prev.filter(article => article.news_id !== id));
                setSelectedNews(prev => prev.filter(selectedId => selectedId !== id));
                toast.success("✅ News article deleted successfully");
            })
            .catch((error) => {
                console.error("Error deleting news article:", error);
            });
    };

    const handleBulkDelete = () => {
        if (selectedNews.length === 0) return;
        if (!window.confirm(`Are you sure you want to delete ${selectedNews.length} selected news articles?`)) return;

        api.post(`/newsfeed/bulk-delete`, { ids: selectedNews })
            .then(() => {
                setNewsArticles(prev => prev.filter(article => !selectedNews.includes(article.news_id)));
                setSelectedNews([]);
                toast.success(`✅ ${selectedNews.length} News articles deleted successfully`);
            })
            .catch((error) => {
                console.error("Error deleting news articles:", error);
                toast.error("Error deleting news articles");
            });
    };

    const newsArticleColumns = [
        {
            key: "select",
            label: (
                <input
                    type="checkbox"
                    checked={newsArticles.length > 0 && selectedNews.length === newsArticles.length}
                    onChange={(e) => {
                        if (e.target.checked) {
                            setSelectedNews(newsArticles.map(article => article.news_id));
                        } else {
                            setSelectedNews([]);
                        }
                    }}
                />
            ),
            render: (_, row) => (
                <input
                    type="checkbox"
                    checked={selectedNews.includes(row.news_id)}
                    onChange={(e) => {
                        if (e.target.checked) {
                            setSelectedNews(prev => [...prev, row.news_id]);
                        } else {
                            setSelectedNews(prev => prev.filter(id => id !== row.news_id));
                        }
                    }}
                />
            )
        },
        { key: "news_id", label: "ID" },
        { key: "title", label: "Title" },
        {
            key: "source_info",
            label: "Source/Date",
            render: (_, row) => {
                const date = formatLocalDate(row.publication_date, "—");
                return (
                    <div className={styles.sourceCell}>
                        <span className={styles.publisherName}>{row.publisher}</span>
                        <div className={styles.dateTime}>
                            <span className={styles.date}>{date}</span>
                            <span className={styles.time}>{row.publish_time}</span>
                        </div>
                    </div>
                );
            }
        },
        {
            key: "brief_description", label: "Brief Description", render: (_, row) => (
                <div
                    className={`${styles.tagsCell} ${styles.maxWidth320}`}
                    dangerouslySetInnerHTML={{
                        __html: row.news_content || row.brief_description || "-",
                    }}
                >
                </div>
            )
        },
        { key: "credit", label: "Credit" },
        {
            key: "tags",
            label: "Tags",
            render: (_, row) => (
                <div className={styles.tagsCell}>
                    <div className={styles.tagItem}>
                        <strong>S:</strong> {Array.isArray(row.tag_stocks) && row.tag_stocks.length > 0 ? row.tag_stocks.join(", ") : ""}
                    </div>

                    <div className={styles.tagItem}>
                        <strong>Sec:</strong> {Array.isArray(row.tags_sectors) && row.tags_sectors.length > 0 ? row.tags_sectors.join(", ") : ""}
                    </div>

                    <div className={styles.tagItem}>
                        <strong>Ind:</strong> {Array.isArray(row.tags_industries) && row.tags_industries.length > 0 ? row.tags_industries.join(", ") : ""}
                    </div>
                </div>
            )
        },
        {
            key: "ispublished",
            label: "Publish Status",
            render: (value) => Number(value) === 1 ? "YES" : "NO"
        },
        {
            key: "published_at",
            label: "Publish At",
            render: (value) =>
                value === null ? '' : formatLocalDateTime(value, ''),
        },
        {
            key: "image_url",
            label: "Image",
            render: (value) => {
                const imageUrl = value?.startsWith("http")
                    ? value
                    : `${REACT_APP_API_BASE_URL}uploads/newsimages/${value}`;

                return (
                    <img
                        src={value === '' || value === null ? altNewsImage : imageUrl}
                        alt=""
                        aria-hidden="true"
                        onClick={() => window.open(value === '' ? altNewsImage : imageUrl, "_blank")}
                        className={styles.imageThumbnail}
                    />
                );
            },
        },
        {
            key: "urls",
            label: "Links",
            render: (_, row) => (
                <div className={`${styles.urlCell} ${styles.flexColGap4}`}>
                    <Button
                        size="small"
                        onClick={() => window.open(row.link, "_blank")}
                        className={`${styles.typeBadge} ${styles.badgeSmall}`}
                    >
                        Link
                    </Button>
                    <Button
                        size="small"
                        onClick={() => window.open(row.source_url, "_blank")}
                        className={`${styles.typeBadge} ${styles.badgeSmall}`}
                    >
                        Source
                    </Button>
                    <Button
                        size="small"
                        onClick={() => window.open(row.destination_url, "_blank")}
                        className={`${styles.typeBadge} ${styles.badgeSmall}`}
                    >
                        Dest
                    </Button>
                </div>
            )
        },
        {
            key: "actions",
            label: "Actions",
            render: (_, row) => (
                <div className={styles.flexCenterGap8}>
                    {hasPermission("MODIFY_NEWSFEED") && (
                        <button
                            className={styles.iconBtn}
                            onClick={() => {
                                setSelectedNewsId(row.news_id);
                                setIsNewsFormOpen(true);
                            }}
                            title="Edit article"
                        >
                            <EditIcon />
                        </button>
                    )}
                    {hasPermission("DELETE_NEWSFEED") && (
                        <button
                            className={`${styles.iconBtn} ${styles.deleteBtn}`}
                            onClick={() => handleDelete(row.news_id)}
                            title="Delete article"
                        >
                            <TrashIcon />
                        </button>
                    )}
                </div>
            ),
        },
    ];

    const fetchInternalTokens = async () => {
        try {
            const response = await newsService.getInternalTokens();
            setInternalTokens(response.data);
        } catch (error) {
            console.error("Error fetching internal tokens:", error);
        }
    };

    const fetchTagSectors = async () => {
        try {
            const response = await newsService.getTagSectors();
            setTagSectors(response.data || []);
        } catch (error) {
            console.error("Error fetching sectors:", error);
        }
    }

    const fetchTagIndustries = async () => {
        try {
            const response = await newsService.getTagIndustries();
            setTagIndustries(response.data || []);
        } catch (error) {
            console.error("Error fetching industries:", error);
        }
    }

    const fetchNews = (overrides = {}) => {
        if (!canViewNews) return;
        const queryParams = {
            page: overrides.page !== undefined ? overrides.page : page,
            limit,
            publish_from: (overrides.publishFrom !== undefined ? overrides.publishFrom : publishFrom) || undefined,
            publish_to: (overrides.publishTo !== undefined ? overrides.publishTo : publishTo) || undefined,
            is_published: overrides.filterPublishStatus !== undefined ? overrides.filterPublishStatus : filterPublishStatus,
            title: (overrides.titleFilter !== undefined ? overrides.titleFilter : titleFilter) || undefined,
            credit: (overrides.creditFilter !== undefined ? overrides.creditFilter : creditFilter) || undefined,
            // credit: (overrides.creditFilter !== undefined ? (overrides.creditFilter ? overrides.creditFilter.value : undefined) : (creditFilter ? creditFilter.value : undefined)),
            // tags: (overrides.tagsFilter !== undefined ? overrides.tagsFilter : tagsFilter) || undefined,
            stock: (overrides.stockFilter !== undefined ? overrides.stockFilter : stockFilter) || undefined,
            sector: (overrides.sectorFilter !== undefined ? overrides.sectorFilter : sectorFilter) || undefined,
            industry: (overrides.industryFilter !== undefined ? overrides.industryFilter : industryFilter) || undefined,
        };

        api.get('/newsfeed', { params: queryParams })
            .then((response) => {
                const articles = response?.data?.data || response?.data || response || [];
                setNewsArticles(Array.isArray(articles) ? articles : []);
                setTotalPages(response?.totalPages || response?.data?.totalPages || 1);
            })
            .catch((error) => {
                console.error('Error fetching news:', error);
            });
    };

    useEffect(() => {
        if (canViewNews) {
            fetchNews();
            setSelectedNews([]); // clear selection when page or filters change
        }
    }, [page, canViewNews]);

    useEffect(() => {
        fetchInternalTokens();
        fetchTagSectors();
        fetchTagIndustries()
    }, []);

    return (
        <div className={styles.newsFeedContainer}>
            <ToastContainer position="bottom-right" autoClose={2000} />

            {/* Tab Navigation */}
            <div className={styles.tabBar}>
                {canViewNews && (
                <button
                    className={`${styles.tabBtn} ${activeTab === 'newsfeed' ? styles.tabBtnActive : ''}`}
                    onClick={() => { setActiveTab('newsfeed'); setPage(1); }}
                >
                    <span className={`material-symbols-outlined ${styles.iconFont18}`}>newspaper</span>
                    News Feed
                </button>
                )}
                {canViewCorporate && (
                <button
                    className={`${styles.tabBtn} ${activeTab === 'corporate' ? styles.tabBtnActive : ''}`}
                    onClick={() => { setActiveTab('corporate'); setPage(1); }}
                >
                    <span className={`material-symbols-outlined ${styles.iconFont18}`}>campaign</span>
                    Corporate Announcements
                </button>
                )}
            </div>

            {activeTab === 'newsfeed' ? (
                <>
            <div className={styles.stickyHeader}>
                <div className={styles.headerTop}>
                    <h2 className={styles.title}>News Articles</h2>
                    <div className={styles.flexCenterGap8}>
                        {hasPermission("DELETE_NEWSFEED") && selectedNews.length > 0 && (
                            <Button 
                                onClick={handleBulkDelete} 
                                className={`${styles.deleteBtn}`}
                                style={{ backgroundColor: '#dc3545', color: 'white' }}
                            >
                                Delete Selected ({selectedNews.length})
                            </Button>
                        )}
                        {hasPermission("CREATE_NEWSFEED") && <Button onClick={() => {
                            setSelectedNewsId(null);
                            setIsNewsFormOpen(true);
                        }}>
                            + Add
                        </Button>}
                    </div>
                </div>

                <div className={`${styles.filterRow} ${styles.alignEnd}`}>
                    <div className={styles.flexColGap2}>
                        <label className={styles.filterLabel}>Title</label>
                        <Input
                            value={titleFilter}
                            onChange={(e) => { setPage(1); setTitleFilter(e.target.value); }}
                            placeholder="Search Title"
                            className={`${styles.filterSelect} ${styles.filterInput180}`}
                        />
                    </div>

                    <div className={styles.flexColGap2}>
                        <label className={styles.filterLabel}>Credit</label>
                        <div className={styles.filterInput180}>
                            <Input
                                value={creditFilter}
                                onChange={(e) => { setPage(1); setCreditFilter(e.target.value); }}
                                placeholder="Search Credit"
                                className={`${styles.filterSelect} ${styles.filterInput180}`}
                            />
                        </div>
                    </div>
                    {/* Stocks filter */}
                    <div className={styles.flexColGap2}>
                        <label className={styles.filterLabel}>Stocks</label>
                        <div className={styles.filterInput180}>
                            <Select
                                value={internalTokens.map((token) => ({
                                    label: `${token.symbol} (${token.name})`,
                                    value: token.token,
                                })).find((opt) => opt.value === stockFilter) || null}
                                onChange={(selected) => { setPage(1); setStockFilter(selected.value); }}
                                options={internalTokens.map((token) => ({
                                    label: `${token.symbol} (${token.name})`,
                                    value: token.token,
                                }))}
                                placeholder="Select Stock"
                                isClearable={false}
                                menuPortalTarget={document.body}
                                menuPosition="fixed"
                                components={{ IndicatorSeparator: () => null, DropdownIndicator: null }}
                                styles={{
                                    control: (base) => ({ ...base, minHeight: '0px' }),
                                    menu: (base) => ({ ...base, zIndex: 9999 }),
                                    menuPortal: (base) => ({ ...base, zIndex: 9999 })
                                }}
                            />
                        </div>
                    </div>
                    {/* Sectors filter */}
                    <div className={styles.flexColGap2}>
                        <label className={styles.filterLabel}>Sectors</label>
                        <div className={styles.filterInput180}>
                            <Select
                                value={tagSectors.map((item) => ({
                                    label: item.sector_name || item,
                                    value: item.sector_name || item,
                                })).find((opt) => opt.value === sectorFilter) || null}
                                onChange={(selected) => { setPage(1); setSectorFilter(selected.value); }}
                                options={tagSectors.map((item) => ({
                                    label: item.sector_name || item,
                                    value: item.sector_name || item,
                                }))}
                                placeholder="Select Sector"
                                isClearable={false}
                                menuPortalTarget={document.body}
                                menuPosition="fixed"
                                components={{ IndicatorSeparator: () => null, DropdownIndicator: null }}
                                styles={{
                                    control: (base) => ({ ...base, minHeight: '0px' }),
                                    menu: (base) => ({ ...base, zIndex: 9999 }),
                                    menuPortal: (base) => ({ ...base, zIndex: 9999 })
                                }}
                            />
                        </div>
                    </div>
                    {/* Industries filter */}
                    <div className={styles.flexColGap2}>
                        <label className={styles.filterLabel}>Industries</label>
                        <div className={styles.filterInput180}>
                            <Select
                                value={tagIndustries.map((item) => ({
                                    label: item.industry_name || item,
                                    value: item.industry_name || item,
                                })).find((opt) => opt.value === industryFilter) || null}
                                onChange={(selected) => { setPage(1); setIndustryFilter(selected.value); }}
                                options={tagIndustries.map((item) => ({
                                    label: item.industry_name || item,
                                    value: item.industry_name || item,
                                }))}
                                placeholder="Select Industry"
                                isClearable={false}
                                menuPortalTarget={document.body}
                                menuPosition="fixed"
                                components={{ IndicatorSeparator: () => null, DropdownIndicator: null }}
                                styles={{
                                    control: (base) => ({ ...base, minHeight: '0px' }),
                                    menu: (base) => ({ ...base, zIndex: 9999 }),
                                    menuPortal: (base) => ({ ...base, zIndex: 9999 })
                                }}
                            />
                        </div>
                    </div>

                    <div className={styles.flexColGap2}>
                        <label className={styles.filterLabel}>Status</label>
                        <div className={styles.filterInput140}>
                            <Select
                                value={filterPublishStatus === '1' ? { value: '1', label: 'Published' } : filterPublishStatus === '0' ? { value: '0', label: 'Not Published' } : null}
                                onChange={(selected) => { setPage(1); setFilterPublishStatus(selected ? selected.value : ""); }}
                                options={[
                                    { value: '', label: 'All Status' },
                                    { value: '1', label: 'Published' },
                                    { value: '0', label: 'Not Published' },
                                ]}
                                placeholder="Select Status"
                                isClearable={false}
                                menuPortalTarget={document.body}
                                menuPosition="fixed"
                                components={{ IndicatorSeparator: () => null, DropdownIndicator: null }}
                                styles={{
                                    control: (base) => ({ ...base, minHeight: '5px' }),
                                    menu: (base) => ({ ...base, zIndex: 9999 }),
                                    menuPortal: (base) => ({ ...base, zIndex: 9999 })
                                }}
                            />
                        </div>
                    </div>

                    <div className={styles.flexColGap2}>
                        <label className={styles.filterLabel}>Publish Date</label>
                        <div className={styles.flexRowGap5}>
                            <Input
                                type="date"
                                value={publishFrom || ''}
                                onChange={(e) => { setPage(1); setPublishFrom(e.target.value); }}
                                placeholder="From"
                                className={`${styles.filterSelect} ${styles.filterInput110}`}
                            />
                            <Input
                                type="date"
                                value={publishTo || ''}
                                onChange={(e) => { setPage(1); setPublishTo(e.target.value); }}
                                placeholder="To"
                                className={`${styles.filterSelect} ${styles.filterInput110}`}
                            />
                        </div>
                    </div>

                    <Button onClick={() => {
                        setPage(1);
                        fetchNews();
                    }}>
                        Search
                    </Button>
                    <Button
                        variant="secondary"
                        onClick={handleClearFilters}
                    >
                        Clear
                    </Button>
                </div>
            </div>

            <DataTable columns={newsArticleColumns} data={newsArticles} />

            {/* Pagination */}
            <div className={styles.imTableFooter}>
                <span>Showing {newsArticles.length} records</span>

                {totalPages > 1 && (
                    <div className={styles.imPagination}>
                        <button
                            type="button"
                            className={styles.imPageBtn}
                            disabled={page === 1}
                            onClick={() => setPage(page - 1)}
                        >
                            {"<"}
                        </button>

                        {Array.from({ length: totalPages }, (_, i) => i + 1).map((p) => {
                            if (
                                p === 1 ||
                                p === totalPages ||
                                (p >= page - 1 && p <= page + 1)
                            ) {
                                return (
                                    <button
                                        type="button"
                                        key={p}
                                        className={`${styles.imPageBtn} ${page === p ? styles.active : ""}`}
                                        onClick={() => setPage(p)}
                                    >
                                        {p}
                                    </button>
                                );
                            }

                            if (p === page - 2 || p === page + 2) {
                                return (
                                    <span key={p} className={styles.paginationEllipsis}>
                                        ...
                                    </span>
                                );
                            }

                            return null;
                        })}

                        <button
                            type="button"
                            className={styles.imPageBtn}
                            disabled={page === totalPages}
                            onClick={() => setPage(page + 1)}
                        >
                            {">"}
                        </button>
                    </div>
                )}

                <span className={`${styles.textMuted} ${styles.textSmall}`}>
                    Page {page} of {totalPages}
                </span>
            </div>
                </>
            ) : (
                <CorporateAnnouncementsTab />
            )}

            {isNewsFormOpen && (
                <FullscreenModal
                    isOpen={isNewsFormOpen}
                    onClose={() => setIsNewsFormOpen(false)}
                    title={selectedNewsId ? "Edit News Article" : "Post a News Article"}
                    maxWidth="910px"
                    disableBackdropClick={true}
                >
                    <NewsForm
                        newsId={selectedNewsId}
                        onSubmitted={() => {
                            setIsNewsFormOpen(false);
                            fetchNews();
                        }}
                        onCancel={() => setIsNewsFormOpen(false)}
                    />
                </FullscreenModal>
            )}
        </div>
    )
}

export default NewsFeed
